import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import Img from 'modules/common/components/Img';
import { RiImageAddLine } from 'react-icons/ri';

const GRID = {
  sm:
    'grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 xl:gap-x-8',
  base:
    'grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 xl:gap-x-8',
  lg:
    'grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-3 xl:gap-x-8',
};

function GridCms({
  size,
  data,
  onAdd,
  onSelect,
  selected,
  labelKey,
  subKey,
  photoKey,
  itemClassName,
}) {
  return (
    <section className="mt-8 ml-1 pb-16" aria-labelledby="gallery-heading">
      <h2 id="gallery-heading" className="sr-only">
        Cms
      </h2>
      <ul className={GRID[size]}>
        {data.length < 1 ? (
          <li className="relative col-span-2 sm:col-span-3 md:col-span-3 lg:col-span-3 xl:col-span-4">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Empty</h3>
              <p className="mt-1 text-sm text-gray-500">There are no items.</p>
            </div>
          </li>
        ) : (
          data.map((row) => (
            <li
              key={row.id}
              className={cn('group relative shadow-primary-sm', itemClassName)}
            >
              <button
                className={cn(
                  'absolute inset-0 h-full w-full focus:outline-none z-10 block',
                  selected === row
                    ? 'ring-2 ring-offset-2 ring-primary-500'
                    : 'focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-gray-100 focus-within:ring-primary-500'
                )}
                type="button"
                onClick={() => onSelect(row)}
              >
                <span className="sr-only">
                  View details for {row[labelKey]}
                </span>
              </button>
              <div className="block w-full aspect-w-7 aspect-h-4 rounded-t-lg overflow-hidden bg-white">
                <Img
                  src={row[photoKey]}
                  alt=""
                  className={cn(
                    selected === row ? '' : 'group-hover:opacity-75',
                    'object-contain object-center pointer-events-none p-2'
                  )}
                />
              </div>
              <div className="bg-gray-50 rounded-b-lg px-3 py-2 text-center">
                <p className="block text-base text-gray-900 font-medium truncate pointer-events-none">
                  {row[labelKey]}
                </p>
                <p className="block text-xs text-gray-500 pointer-events-none truncate">
                  {row[subKey]}
                </p>
              </div>
            </li>
          ))
        )}
        {typeof onAdd === 'function' ? (
          <li className="relative shadow-primary-sm aspect-h-1 aspect-w-1">
            <button
              className="absolute inset-0 h-full w-full focus:outline-none z-10 block border-dashed border-2 rounded-lg bg-white text-gray-600 hover:text-primary-500 hover:border-primary-500 transition"
              type="button"
              onClick={onAdd}
            >
              <RiImageAddLine className="w-6 h-6 mx-auto mb-2" />
              <span>Add</span>
            </button>
          </li>
        ) : null}
      </ul>
    </section>
  );
}

GridCms.defaultProps = {
  data: [],
  size: 'base',
  subKey: 'type',
  selected: null,
  labelKey: 'label',
  onSelect: () => {},
  photoKey: 'photo_url',
  itemClassName: '',
  onAdd: false,
};

GridCms.propTypes = {
  size: PropTypes.string,
  onSelect: PropTypes.func,
  subKey: PropTypes.string,
  labelKey: PropTypes.string,
  photoKey: PropTypes.string,
  itemClassName: PropTypes.string,
  selected: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Object),
  ]),
  data: PropTypes.instanceOf(Array),
  onAdd: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
};

export default GridCms;
